/**
 * React Query hooks for placements.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { placementApi } from '@/api/client';
import type { PlacementCreate, PlacementUpdate, ShareUrlRequest } from '@/api/schemas';

export function usePlacements(params?: { domainId?: string; q?: string; page?: number }) {
  return useQuery({
    queryKey: ['placements', params],
    queryFn: () => placementApi.listPlacements(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function usePlacement(placementId: string) {
  return useQuery({
    queryKey: ['placement', placementId],
    queryFn: () => placementApi.getPlacement(placementId),
    enabled: !!placementId,
    staleTime: 30 * 1000,
  });
}

export function useCreatePlacement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlacementCreate) => placementApi.createPlacement(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['placements'] });
    },
  });
}

export function useUpdatePlacement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ placementId, data }: { placementId: string; data: PlacementUpdate }) =>
      placementApi.updatePlacement(placementId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['placement', variables.placementId] });
      queryClient.invalidateQueries({ queryKey: ['placements'] });
    },
  });
}

export function useCreateShareUrl() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ placementId, data }: { placementId: string; data: ShareUrlRequest }) =>
      placementApi.createShareUrl(placementId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['share-urls', variables.placementId] });
    },
  });
}

